import { memo, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useLocation } from 'react-router-dom';
import { Button, Heading, HStack, Text, VStack } from '@library';
import type { Bicycle, ProductVehicle } from '../../types';
import CollapsibleContent from '../../../core/components/CollapsibleContent';
import FeaturedVehicle from '../ProductList/Filters/FeaturedVehicle';
import useVehicle from '../../hooks/useVehicle';
import notEmpty from '../../../core/util/notEmpty';
import ProductList from '../ProductList/List/ProductList';
import CollapsibleSideBarFilterTitle from '../ProductList/Filters/CollapsibleSideBarFilterTitle';
import withErrorBoundary from '../../../core/components/errors/withErrorBoundary';
import assert from '../../../core/util/assert';
import Container from '../../../core/components/Container';
import MotorcycleSearchMetadata from '../MotorcycleSearchMetadata';
import CheckmarkIcon from '../../../core/components/icons/CheckmarkIcon';
import ProductListCategoryTiles from '../ProductList/Header/ProductListCategoryTiles';
import ProductListTireSearch, {
    useTireSearchEnabled,
} from '../TireSearchNew/ProductListTireSearch';
import useCategory from '../../../core/queries/useCategory';
import { tireFiltersIgnoreList } from '../TireSearchNew';
import usePagination from '../../hooks/usePagination';
import ProductSearchSidePanel from '../ProductSearchSidePanel';
import MotorcycleSearchSidePanelContent from '../MotorcycleSearchSidePanelContent';
import styles from './MotorcycleSearchResults.module.css';

export const VehicleSearchFeaturedFilter = memo(
    ({
        vehicle,
        isIntercycle = false,
        isMotorcycle = false,
    }: {
        vehicle: Bicycle | ProductVehicle;
        isIntercycle?: boolean;
        isMotorcycle?: boolean;
    }) => {
        const intl = useIntl();
        const messageId = isIntercycle ? 'bicycle' : 'spare parts search';
        return (
            <CollapsibleContent
                expanded
                smallIcon
                endArrow
                filterIcon
                title={
                    <CollapsibleSideBarFilterTitle>
                        {intl.formatMessage({
                            id: isMotorcycle ? 'motorcycle' : messageId,
                        })}
                    </CollapsibleSideBarFilterTitle>
                }
            >
                <FeaturedVehicle vehicle={vehicle} isBicycle={isIntercycle} />
            </CollapsibleContent>
        );
    }
);

export const VehicleSearchHeader = memo(
    ({ vehicle }: { vehicle?: ProductVehicle }) => {
        const [isSidePaneOpen, setIsSidePaneOpen] = useState(false);
        const [showModelUpdatedLabel, setShowModelUpdatedLabel] =
            useState(false);

        const toggleSidePane = () => {
            setIsSidePaneOpen(!isSidePaneOpen);
        };

        const { navigateToPage } = usePagination();
        const handleModelChange = (vehicleId: string) => {
            navigateToPage(1, `vehicleId=${vehicleId}`);

            setIsSidePaneOpen(false);
            setShowModelUpdatedLabel(true);
        };

        useEffect(() => {
            let timer: ReturnType<typeof setTimeout>;

            if (showModelUpdatedLabel) {
                timer = setTimeout(() => {
                    setShowModelUpdatedLabel(false);
                }, 5000);
            }
            return () => clearTimeout(timer);
        }, [showModelUpdatedLabel]);

        return (
            <div className={styles.vehicleSearchHeader}>
                <VStack gap="smallMedium">
                    <div className={styles.vehicleSearchMetadataHeader}>
                        <MotorcycleSearchMetadata vehicle={vehicle} />

                        <HStack gap="medium" align="center">
                            {showModelUpdatedLabel && (
                                <HStack gap="micro">
                                    <CheckmarkIcon color="#647FB1" />
                                    <Text component="h3" color="info">
                                        <FormattedMessage id="Motorcycle Search Model Changed" />
                                    </Text>
                                </HStack>
                            )}
                            <Button
                                outline
                                onClick={toggleSidePane}
                                data-test-class={'changeModel'}
                            >
                                <FormattedMessage id="Motorcycle Search Change Model" />
                            </Button>
                        </HStack>

                        <ProductSearchSidePanel
                            isOpen={isSidePaneOpen}
                            toggle={toggleSidePane}
                        >
                            <MotorcycleSearchSidePanelContent
                                title="Motorcycle Search Change Model"
                                onModelChange={handleModelChange}
                                showSelectedVehicle
                            />
                        </ProductSearchSidePanel>
                    </div>
                    <ProductListCategoryTiles />
                </VStack>
            </div>
        );
    }
);

const useSearchParams = () => {
    const { search } = useLocation();
    const searchParams = new URLSearchParams(search);
    return {
        searchParams,
    };
};

const CategoryHeader = memo(() => {
    const { categoryId, categoryName } = useCategory();

    return (
        <Heading h3 bold dataTestClass={'categoryHeader'}>
            {categoryId ? (
                categoryName
            ) : (
                <FormattedMessage id="all categories" />
            )}
        </Heading>
    );
});

const ignoreList = ['vehicleId', 'bicycleId'];

const MotorcycleSearchResults = memo(() => {
    const { searchParams } = useSearchParams();
    const { vehicle, vehicleIdExists, loading } = useVehicle();
    const intl = useIntl();

    assert(vehicleIdExists && (vehicle || loading), 'Wrong Vehicle Id');
    const { enabled } = useTireSearchEnabled();

    const title = intl.formatMessage(
        {
            id: 'Vehicle ProductListPage Title',
        },
        {
            vehicleModel: `${vehicle?.model} ${vehicle?.manufacturerName}`,
        }
    );

    return (
        <ProductList
            title={title}
            isFiltersLoading={loading}
            ignoreList={[
                ...ignoreList,
                ...(enabled ? ['bicycleId', ...tireFiltersIgnoreList] : []),
            ]}
            topBanner={
                <VehicleSearchHeader vehicle={vehicle as ProductVehicle} />
            }
            filters={{
                includeExcludedCategories: false,
            }}
            header={<CategoryHeader />}
            banner={<ProductListTireSearch />}
            outerLoading={loading}
            rootLinks={[
                {
                    messageId: 'motorcycle search',
                    to: '/motorcycle-search',
                },
                ...(notEmpty(vehicle)
                    ? [
                          {
                              text: `${vehicle.manufacturerName} ${vehicle.model}`,
                              to: `/vehicle-search?${searchParams.toString()}`,
                          },
                      ]
                    : []),
            ]}
            isSearchResultPage
        />
    );
});

export default memo(
    withErrorBoundary(MotorcycleSearchResults, {
        errorComponent: ({ defaultErrorMessage }) => (
            <Container>
                <br />
                {defaultErrorMessage}
            </Container>
        ),
    })
);
